import apiCall from "@/utils/api";
import { useQuery } from "@tanstack/vue-query";
import { getCurrentInstance, reactive, watch } from "vue";
import i18n from "@/plugins/i18n";

const t = i18n.t.bind(i18n);
const tc = i18n.tc.bind(i18n);

export function checkHeightOverflowed(el) {
  if (el.clientHeight < el.scrollHeight) {
    return true;
  } else {
    return false;
  }
}

export function debounce(func, wait = 300) {
  let timeout;

  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      func.apply(this, args);
    }, wait);
  };
}

export function useRoute() {
  const { proxy } = getCurrentInstance();
  const route = proxy.$route;
  return route;
}

export function useRouter() {
  const { proxy } = getCurrentInstance();
  const router = proxy.$router;
  return router;
}

export function useStore() {
  const { proxy } = getCurrentInstance();
  const store = proxy.$store;
  return store;
}

export function useGetTranslation(plural = false) {
  return plural ? tc : t;
}

export function useFetchResourceUsingId(resourceName, id) {
  return useQuery({
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    queryKey: ["show", resourceName, id],
    queryFn: async () => {
      const user = await apiCall({
        url: `/api/${resourceName}/${id}`,
        method: "GET",
      });
      return user;
    },
  });
}

export function useFetchAllPermissions({ normalize = false } = {}) {
  return useQuery({
    refetchOnWindowFocus: false,
    queryKey: ["allPermissions"],
    queryFn: async () => {
      const result = await apiCall({
        url: "/api/permission?paginate=false",
        method: "GET",
      });
      if (normalize) {
        return convertArrayOfObjsToObj(result, "id");
      }
      return result;
    },
  });
}

export function useFetchAllResource({ resourceName, normalize = false }) {
  return useQuery({
    refetchOnWindowFocus: false,
    queryKey: [`all${resourceName}`],
    queryFn: async () => {
      const result = await apiCall({
        url: `/api/${resourceName}?paginate=false`,
        method: "GET",
      });
      if (normalize) {
        return convertArrayOfObjsToObj(result, "id");
      }
      return result;
    },
  });
}

export function watchRoute(callback, watchOptions) {
  const { proxy } = getCurrentInstance();
  watch(
    () => proxy.$route,
    (newRoute, oldRoute) => {
      callback(newRoute, oldRoute);
    },
    watchOptions
  );
}

export function getUsername(user) {
  let name = "";
  if(user.first_name){
    name += user.first_name + " "
  }
  if(user.middle_name){
    name += user.middle_name + " "
  }
  if(user.last_name){
    name += user.last_name + " "
  }
  return name
}

export function convertArrayOfObjsToObj(arr, keyedBy) {
  return arr.reduce((prevVal, currVal) => {
    prevVal[currVal[keyedBy]] = currVal;
    return prevVal;
  }, {});
}

export function createSeededRng(seed, min, max) {
  // A simple PRNG (Pseudorandom Number Generator) based on a seed.
  let currentSeed = seed;

  // Function to update the seed
  function next() {
    currentSeed = (currentSeed * 16807) % 2147483647; // Linear Congruential Generator (LCG)
    return currentSeed / 2147483647; // Normalize the output to [0, 1)
  }

  // The returned function generates random numbers within the specified range [min, max]
  return function() {
    // Generate a random number between min and max (inclusive of min, exclusive of max)
    return Math.floor(next() * (max - min + 1)) + min;
  };
}
